/**
 * src/pages/auth/ResetPasswordPage.tsx
 *
 * The /auth/reset routed page — the second half of the password-reset round trip started
 * by RequestPasswordResetForm. Public route, hoisted ABOVE RequireAuth in App.tsx (sibling
 * of /auth/confirm) — an anonymous visitor arriving from an email link must reach this page;
 * RequireAuth would otherwise substitute LandingPage in its place.
 *
 * Structurally copies ConfirmPage.tsx's shell (header + Logo, centered motion.div card,
 * min-h-screen background) but is a PLAIN controlled form reading the token via
 * useSearchParams — NOT ConfirmPage's two-step click-to-spend construction. That construction
 * exists to survive email-scanner GET prefetch; here the token is only ever spent by this
 * form's POST, so a GET (including a prefetch) is harmless (43-CONTEXT.md Область 1).
 *
 * PWD-04 — the hard half, verified literally: a successful reset never mints or refreshes a
 * session. No signIn call, no session refetch, no auto-login — resetPassword only changes
 * the password hash and returns no session token (better-auth/dist/api/routes/password.mjs).
 * The best-effort half (email pre-fill) is carried through router navigation state only,
 * reading (and immediately clearing) RequestPasswordResetForm's one-shot sessionStorage key —
 * never localStorage, continuing the Phase 42 prohibition.
 *
 * PWD-05 — one honest message for a dead link. better-auth's resetPassword collapses an
 * expired token and an already-consumed token into the SAME INVALID_TOKEN condition — it
 * genuinely cannot tell them apart (consumeVerificationValue returns null for both), so
 * inventing a distinction in this UI would be a fabricated claim. `invalidTokenNode` below is
 * the single translation call site for that shared copy, rendered from BOTH entry points (a
 * missing/empty/whitespace token at mount, and a server INVALID_TOKEN on submit) — never
 * duplicated, so a future edit cannot let the two drift apart.
 */
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '@/shared/ui/button';
import { Logo } from '@/shared/ui/Logo';
import { authClient } from '@/lib/auth-client';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import { MIN_PASSWORD_LENGTH } from '@/features/auth/lib/passwordPolicy';
import { PENDING_RESET_EMAIL_KEY } from '@/features/auth/ui/RequestPasswordResetForm';
import { broadcastSessionRevoked } from '@/features/auth/lib/authBroadcast';
// WR-03 (43-REVIEW.md): this page is a genuine cross-slice consumer of the auth feature — it
// lives in src/pages/auth/, outside src/features/auth/ — so it imports the shared
// PasswordField through the slice's public API, not a direct submodule path.
import { PasswordField } from '@/features/auth';

/** Detects better-auth's resetPassword INVALID_TOKEN condition (thrown identically for an
 * expired AND an already-consumed token — password.mjs's consumeVerificationValue returns
 * null for both, so there is nothing to distinguish). Primary signal is the error `code`;
 * the `status`+message fallback covers an intermediary that strips `code` while preserving
 * the 400 Bad Request.
 *
 * WR-02 (43-REVIEW.md): the fallback used to be `message.includes('token')`, which matched
 * ANY 400 whose message merely contained that substring (a CSRF-token failure, a future
 * rate-limit message, etc.) — broader than what it was meant to recognize. Narrowed to an
 * exact (trimmed, case-insensitive) match against the ONE message better-auth's APIError
 * actually produces at this throw site: `BASE_ERROR_CODES.INVALID_TOKEN.message`, verified
 * against installed source (node_modules/@better-auth/core/dist/error/codes.mjs) to be the
 * literal string "Invalid token" — not a guess. */
function isInvalidTokenError(error: { code?: string; status?: number; message?: string } | null | undefined): boolean {
  if (!error) return false;
  if (error.code === 'INVALID_TOKEN') return true;
  return (
    error.status === 400 &&
    typeof error.message === 'string' &&
    error.message.trim().toLowerCase() === 'invalid token'
  );
}

const ResetPasswordPage: FC = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const token = params.get('token');
  // Entry point one (PWD-05): unusable when null, empty, or whitespace-only — trimmed
  // BEFORE the emptiness test, per 43-CONTEXT.md Область 1.
  const trimmedToken = (token ?? '').trim();

  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Entry point two: flipped by the submit handler on a server INVALID_TOKEN result.
  const [tokenInvalidatedByServer, setTokenInvalidatedByServer] = useState(false);
  // A non-INVALID_TOKEN failure (network, or any other error shape) — separate message,
  // form stays mounted. Never shares a node or a key with the invalid-link message below.
  const [generalError, setGeneralError] = useState<string | null>(null);

  const showInvalidToken = trimmedToken === '' || tokenInvalidatedByServer;

  // Synchronous in-flight guard — same idiom and rationale as PasswordSignInForm.tsx:39-43.
  // This is the client half of the criterion-5 double-submit fix (the server half is the
  // partial UNIQUE index shipped by a separate plan in this phase).
  const inFlightRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightRef.current) return;
    if (password.length < MIN_PASSWORD_LENGTH) return;

    inFlightRef.current = true;
    setIsLoading(true);
    setGeneralError(null);

    // WR-01: better-auth returns { data, error }, never throws.
    const result = await authClient.resetPassword({ newPassword: password, token: trimmedToken });
    if (!result.error) {
      // PWD-06: announce, not revoke — the server already revoked every OTHER session for
      // this user (revokeSessionsOnPasswordReset, Phase 40). This posts the second message
      // type on the shared estimatix-auth channel while the page is still mounted, before
      // reading the relay key or navigating away. This tab does not see its own notice
      // (isSelfOrigin in useAuth.ts's listener), which is correct: it is not signed in here
      // either way (resetPassword mints no session — PWD-04, hard half, above).
      broadcastSessionRevoked();

      // Best-effort email relay (43-CONTEXT.md Область 1-бис) — read then immediately clear
      // the one-shot key RequestPasswordResetForm wrote before sending the reset email.
      // Absent in the common cross-tab/cross-device case: that is the expected, non-error
      // state, not a fallback for a defect.
      let email: string | undefined;
      try {
        email = sessionStorage.getItem(PENDING_RESET_EMAIL_KEY) ?? undefined;
        sessionStorage.removeItem(PENDING_RESET_EMAIL_KEY);
      } catch {
        email = undefined;
      }
      // No signIn()/refetch() call — resetPassword mints no session (PWD-04, hard half).
      // replace: true takes the token-bearing URL out of the history stack.
      navigate('/', { state: { email }, replace: true });
      return;
    }

    if (isInvalidTokenError(result.error)) {
      setTokenInvalidatedByServer(true);
    } else {
      setGeneralError(t('auth.reset.error', "Couldn't reset your password. Please try again."));
    }
    inFlightRef.current = false;
    setIsLoading(false);
  };

  // Phase 43 Task 2 — routes back to the reset-request form. /auth/reset is a standalone
  // page and the form lives inside LoginCard, so this reuses the same navigation-state
  // channel Task 1 established (rather than a second one) — LoginCard's lazy state read is
  // extended to honour `openRequestReset` alongside the existing `email` relay.
  const requestNewLink = (
    <Button
      variant="link"
      onClick={() => navigate('/', { state: { openRequestReset: true } })}
      className="self-center"
    >
      {t('auth.reset.request_new', 'Request a new reset email')}
    </Button>
  );

  // Single translation call site for the invalid-link copy (PWD-05) — rendered from BOTH
  // entry points via the shared `showInvalidToken` boolean, never duplicated.
  const invalidTokenNode = (
    <div className="flex flex-col gap-3 text-center">
      <p className="text-body text-red-600 dark:text-red-400" role="alert">
        {t('auth.reset.invalid_token', 'This reset link is no longer valid. Request a new one.')}
      </p>
      {requestNewLink}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans flex flex-col">
      <header className="h-20 flex items-center px-6 md:px-12">
        <Logo className="h-9 w-auto" />
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <motion.div
          className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-card p-6 flex flex-col gap-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduced ? 0 : 0.2 }}
        >
          {showInvalidToken ? (
            invalidTokenNode
          ) : (
            <>
              <p className="text-subhead font-bold text-slate-900 dark:text-slate-100 text-center">
                {t('auth.reset.title', 'Set a new password')}
              </p>
              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <PasswordField
                  value={password}
                  onChange={setPassword}
                  label={t('auth.reset.new_password_label', 'New password')}
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <Button
                  variant="default"
                  size="lg"
                  type="submit"
                  className="w-full"
                  disabled={isLoading || password.length < MIN_PASSWORD_LENGTH}
                  aria-busy={isLoading}
                >
                  {isLoading ? t('auth.submit_loading', 'Sending...') : t('auth.reset.cta', 'Set new password')}
                </Button>
                {generalError && (
                  <p
                    className="text-body text-red-600 dark:text-red-400 text-center"
                    role="alert"
                  >
                    {generalError}
                  </p>
                )}
              </form>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
